"use client";
import { useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslations } from "next-intl";

import { Modal } from "@/components/common";
import {
  Category,
  DeliveryPlace,
  ShopWithCountry,
  TransportationWrapper,
} from "@/components/dashboard/parcels/get-parcels-table/components";
import ArrivedParcelsFilterBox from "@/components/dashboard/parcels/parcels-filter/arrived-parcels-filter-box";
import ParcelsTable from "@/components/dashboard/parcels/get-parcels-table/get-parcels-table";

import { useParcelsRequest } from "@/api/useParcelsRequest";
import { ParcelStatus, PlaceOfDelivery, Theme } from "@/enums";

import { IShipment } from "@/types/apiResponse.types";
import { parcelsStatusses } from "@/utils/additional";
import { useGetDeliveryAddressesForShipmentRequest } from "@/api/useGetDeliveryAddressesForShipmentRequest";
import ArrivedParcelsCourierModal from "@/components/dashboard/parcels/ArrivedParcelsCourierModal";
import { useModal } from "@/hooks/useModal";
import Badge from "@/components/common/badge/Badge";
import { useTheme } from "@/context/Theme.context";
import { isInDateRange } from "@/utils/parcel-filter.utils";

export type {
  IDateRangeFilter,
  IParcelFilter,
  IFilterOption,
} from "@/types/parcel-filter.types";
import type {
  IDateRangeFilter,
  IParcelFilter,
} from "@/types/parcel-filter.types";

const columnHelper = createColumnHelper<IShipment>();

export const deliveredStatusses = [
  ParcelStatus.COMMON_STATUS_READY_FOR_PICKUP,
  ParcelStatus.COMMON_STATUS_DELIVERY_REQUEST,
  ParcelStatus.COMMON_STATUS_TRANSFERRED_TO_NATIONAL_CARRIER,
];

const ArrivedParcels = () => {
  const [filters, setFilters] = useState<IParcelFilter>({
    country: [],
    category: [],
    transport: [],
    status: [
      ParcelStatus.COMMON_STATUS_READY_FOR_PICKUP,
      ParcelStatus.COMMON_STATUS_DELIVERY_REQUEST,
      ParcelStatus.COMMON_STATUS_TRANSFERRED_TO_NATIONAL_CARRIER,
    ],
    registrationDate: { start: null, end: null },
    sendDate: { start: null, end: null },
    deliveryDate: { start: null, end: null },
  });
  const [search, setSearch] = useState("");

  const theme = useTheme();

  const t = useTranslations();

  const { closeModal, openModal, modalOpen } = useModal();

  const { data: parcelsData, isLoading } = useParcelsRequest();

  const { data: courierData } = useParcelsRequest({
    shipmentStatus: ParcelStatus.COMMON_STATUS_DELIVERY_REQUEST,
  });
  const { data: officeData } = useGetDeliveryAddressesForShipmentRequest();

  const departmentsList = useMemo(() => {
    if (!officeData) return null;

    return officeData?.data?.data?.filter(
      (item) => item?.addressBaseType === "department",
    );
  }, [officeData]);

  const parcelsList = useMemo(() => {
    if (!parcelsData?.data?.data?.length) {
      return [];
    }
    return parcelsData?.data?.data.filter((item) =>
      parcelsStatusses.arrived.includes(item.commonStatusCode),
    );
  }, [parcelsData]);

  const filteredParcelsData = useMemo<IShipment[]>(() => {
    if (!parcelsList) return [];

    return parcelsList.filter((item) => {
      const isCountryMatch =
        filters.country.length === 0 ||
        filters.country.includes(item.countryId);
      const isCategoryMatch =
        filters.category.length === 0 ||
        filters.category.includes(item?.shipmentTypeId);
      const isTransportMatch =
        filters.transport.length === 0 ||
        filters.transport.includes(item.deliveryFromFromcountryTypeId);

      const isStatusMatch =
        filters.status.length === 0 ||
        filters.status.includes(item.commonStatusCode);

      const searchLower = search.toLowerCase();
      const isSearchMatch =
        item?.trCode?.toLowerCase().includes(searchLower) ||
        item?.description?.toLowerCase().includes(searchLower) ||
        item?.onlineShop?.toLowerCase().includes(searchLower);

      const isRegistrationDateMatch = isInDateRange(
        item.dateCreated?.date,
        filters.registrationDate,
      );
      const isSendDateMatch = isInDateRange(
        item.dateShippedFromFCountry?.date,
        filters.sendDate,
      );
      const isDeliveryDateMatch = isInDateRange(
        item.dateDelivered?.date,
        filters.deliveryDate,
      );

      return (
        isCountryMatch &&
        isCategoryMatch &&
        isTransportMatch &&
        isSearchMatch &&
        isStatusMatch &&
        isRegistrationDateMatch &&
        isSendDateMatch &&
        isDeliveryDateMatch
      );
    });
  }, [parcelsList, filters, search]);

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor((row) => row, {
        id: "shop",
        header: () => <></>,
        cell: (info) => (
          <ShopWithCountry
            shop={info.getValue()?.onlineShop}
            country={info.getValue()?.countryId}
          />
        ),
        size: 10,
      }),
      columnHelper.accessor((row) => row?.trCode, {
        id: "trCode",
        header: () => <span>{t("tracking_number")}</span>,
        cell: (info) => <span>{info.getValue() || t("no_tracking_code")}</span>,
      }),
      columnHelper.accessor(
        (row) =>
          theme === Theme.UK ? row?.shipmentTypeParentId : row?.shipmentTypeId,
        {
          id: "shipmentTypeId",
          header: () => <span>{t("category")}</span>,
          cell: (info) => (
            <div className="pl-4">
              <Category category={info.getValue()} />
            </div>
          ),
        },
      ),
      columnHelper.accessor((row) => row?.declaredPriceUsd, {
        id: "priceUsd",
        header: () => <span>{t("product_price")}</span>,
        cell: (info) => <span>${info.getValue() || 0}</span>,
      }),
      ...(theme === Theme.UK
        ? [
            columnHelper.accessor((row) => row?.description, {
              id: "description",
              header: () => <span>{t("description")}</span>,
              cell: (info) => <span>{info.getValue() || ""}</span>,
            }),
          ]
        : []),
      columnHelper.accessor((row) => row?.readyForPickupDate?.date, {
        id: "transportationInfo",
        header: () => <span>{t("transportation_info")}</span>,
        cell: (info) => {
          return (
            <TransportationWrapper
              label="arrival_date"
              mode={info?.row?.original?.deliveryFromFromcountryTypeId}
              estimatedArrival={info.getValue() || null}
              countryId={info?.row?.original?.countryId}
            />
          );
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "placeOfDelivery",
        header: () => <span> {t("place_of_delivery")}</span>,
        cell: (info) => {
          const trCode = info?.row?.original?.trCode;

          const isCourierDelivery = courierData?.data?.data.find(
            (item) => item.trCode === trCode,
          );

          const department =
            departmentsList && info?.row?.original?.outstockDepartment
              ? departmentsList?.find(
                  (item) =>
                    item.addressId === info?.row?.original?.outstockDepartment,
                )
              : null;

          return (
            <DeliveryPlace
              mode={
                isCourierDelivery
                  ? PlaceOfDelivery.COURIER
                  : PlaceOfDelivery.OFFICE
              }
              address={
                isCourierDelivery
                  ? ""
                  : department
                    ? `${`${department?.cityId ? `${department?.cityId}, ` : ""} ` || ""} ${department?.streetName}`
                    : ""
              }
            />
          );
        },
      }),
    ];

    baseColumns.push(
      columnHelper.accessor((row) => row, {
        id: "subStatus",
        header: () => <span>{t("subStatus")}</span>,
        cell: (info) => {
          const row = info?.row?.original;
          // const isRequiredCustomsDuty =
          //   row?.isDetainedAtCustoms || row?.isDutyCharged;

          return (
            <Badge
              // isDeclared={row?.isDeclared}
              parcelStatus={row?.commonStatusCode}
            />
          );
        },
      }),
    );

    return baseColumns;
  }, [parcelsList, courierData?.data?.data, theme]);

  const filterChangeHandler = (updatedFilters: IParcelFilter) => {
    setFilters(updatedFilters);
  };

  const searchHandler = (search: string) => {
    setSearch(search);
  };

  return (
    <>
      <ArrivedParcelsFilterBox
        filters={filters}
        onFilterChange={filterChangeHandler}
        searchValue={search}
        onSearchChange={searchHandler}
        openModal={openModal}
      />
      <ParcelsTable
        columns={columns}
        data={filteredParcelsData}
        isLoading={isLoading}
      />
      <Modal
        title="orderCourier"
        isOpen={modalOpen}
        closeModal={closeModal}
        paddings="!p-0"
        className="w-full lg:w-[800px]"
        showCloseIcon
      >
        <ArrivedParcelsCourierModal closeModal={closeModal} />
      </Modal>
    </>
  );
};

export default ArrivedParcels;
